import {
    ChangeEvent, useEffect, useRef, useCallback, useMemo, useState,
} from 'react';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import { config } from 'config';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { MAP_STYLE_URLS, applyMapStyle } from 'components/Map/mapStyles';
import { applySelectionEmphasis, installRuntimeLayers, loadMarkerImages } from '../mapLayers';
import { Deal } from '../../types';
import {
    MAP_BEHAVIOR, MAP_LAYERS, MAP_SOURCE_ID,
} from '../components/mapTheme';
import { DealHoverPopupController } from '../components/dealHoverPopup';
import { DealColorLookup } from './useColoredDealIds';
import {
    type DealCoordinates,
    getDealCoords,
    buildGeoJSON,
    getBoundsFromGeoJSON,
} from '../../dealsMapGeoJson';

mapboxgl.accessToken = config.mapboxGlApiKey;

interface UseDealsMapboxMapArgs {
    deals: Deal[];
    geocodedCoordinates: Record<number, DealCoordinates>;
    selectedDealId: number | null;
    hoveredDealId: number | null;
    colorByDealId?: DealColorLookup;
    onSelectDeal: (deal: Deal) => void;
    onDeselectDeal: () => void;
    onHoverDeal: (dealId: number | null) => void;
}

/** Owns the deals-page Mapbox map: clustered pins, hover popups, selection emphasis, and the satellite style toggle. */
export const useDealsMapboxMap = ({
    deals,
    geocodedCoordinates,
    selectedDealId,
    hoveredDealId,
    colorByDealId,
    onSelectDeal,
    onDeselectDeal,
    onHoverDeal,
}: UseDealsMapboxMapArgs) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const dealHoverPopupRef = useRef(new DealHoverPopupController());
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dealsRef = useRef<Deal[]>(deals);
    const geocodedRef = useRef<Record<number, DealCoordinates>>(geocodedCoordinates);
    const colorByDealIdRef = useRef<DealColorLookup | undefined>(colorByDealId);
    const onSelectDealRef = useRef(onSelectDeal);
    const onDeselectDealRef = useRef(onDeselectDeal);
    const onHoverDealRef = useRef(onHoverDeal);
    const didInitialBoundsFitRef = useRef(false);
    const selectedDealIdRef = useRef<number | null>(selectedDealId);
    const hoveredDealIdRef = useRef<number | null>(hoveredDealId);
    const styleGenerationRef = useRef(0);
    const [isSatellite, setIsSatellite] = useState(false);

    dealsRef.current = deals;
    geocodedRef.current = geocodedCoordinates;
    colorByDealIdRef.current = colorByDealId;
    onSelectDealRef.current = onSelectDeal;
    onDeselectDealRef.current = onDeselectDeal;
    onHoverDealRef.current = onHoverDeal;
    selectedDealIdRef.current = selectedDealId;
    hoveredDealIdRef.current = hoveredDealId;

    const geojson = useMemo(
        () => buildGeoJSON(deals, geocodedCoordinates, colorByDealId),
        [deals, geocodedCoordinates, colorByDealId],
    );

    const geojsonRef = useRef(geojson);
    geojsonRef.current = geojson;

    const handleFitAll = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = getBoundsFromGeoJSON(buildGeoJSON(dealsRef.current, geocodedRef.current, colorByDealIdRef.current));
        if (bounds) {
            map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
        }
        onDeselectDealRef.current();
    }, []);

    const handleToggleSatellite = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const map = mapRef.current;
        if (!map) return;
        setIsSatellite(event.target.checked);
        // setStyle() tears down the current style synchronously; invalidate any setup still awaiting marker images.
        styleGenerationRef.current += 1;
        applyMapStyle(map, event.target.checked);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const dealHoverPopup = dealHoverPopupRef.current;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: MAP_STYLE_URLS.regular,
            center: [DEFAULT_NYC_COORDINATES.lng, DEFAULT_NYC_COORDINATES.lat],
            zoom: MAP_BEHAVIOR.minZoom,
            attributionControl: false,
            pitch: 0,
            maxPitch: 60,
        });

        mapRef.current = map;

        const handlePinClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!e.features || e.features.length === 0) return;
            e.originalEvent.stopPropagation();
            const dealId = e.features[0].properties?.id;
            const deal = dealsRef.current.find(d => d.id === dealId);
            if (deal) onSelectDealRef.current(deal);
        };

        const handleClusterClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!e.features || e.features.length === 0 || !mapRef.current) return;
            const feature = e.features[0];
            const clusterId = feature.properties?.cluster_id;
            const source = mapRef.current.getSource(MAP_SOURCE_ID) as GeoJSONSource;
            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || zoom == null || !mapRef.current) return;
                const geometry = feature.geometry as GeoJSON.Point;
                mapRef.current.easeTo({
                    center: geometry.coordinates as [number, number],
                    zoom,
                });
            });
        };

        const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
            if (!mapRef.current) return;
            // Querying a layer id the current style doesn't have raises a map error, and mid-rebuild none of them exist yet.
            const presentLayers = [MAP_LAYERS.unclusteredPoint, MAP_LAYERS.clusters, MAP_LAYERS.clusterCount]
                .filter(layerId => mapRef.current?.getLayer(layerId));
            const features = presentLayers.length > 0
                ? mapRef.current.queryRenderedFeatures(e.point, { layers: presentLayers })
                : [];
            if (features.length === 0) {
                onDeselectDealRef.current();
            }
        };

        const handlePinMouseEnter = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
            if (!mapRef.current || !e.features || e.features.length === 0) return;
            const mapInstance = mapRef.current;
            mapInstance.getCanvas().style.cursor = 'pointer';
            const feature = e.features[0];
            const dealId = feature.properties?.id;
            if (dealId != null) onHoverDealRef.current(dealId);
            dealHoverPopupRef.current.show(mapInstance, feature);
        };

        const handlePinMouseLeave = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = '';
            onHoverDealRef.current(null);
            dealHoverPopupRef.current.remove();
        };

        const handleClusterMouseEnter = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = 'pointer';
        };

        const handleClusterMouseLeave = () => {
            if (!mapRef.current) return;
            mapRef.current.getCanvas().style.cursor = '';
        };

        map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right');

        // Layer-scoped listeners are inert until their layers exist and survive style rebuilds, so one registration here never duplicates.
        map.on('click', MAP_LAYERS.unclusteredPoint, handlePinClick);
        map.on('click', MAP_LAYERS.clusters, handleClusterClick);
        map.on('click', handleMapClick);
        map.on('mouseenter', MAP_LAYERS.unclusteredPoint, handlePinMouseEnter);
        map.on('mouseleave', MAP_LAYERS.unclusteredPoint, handlePinMouseLeave);
        map.on('mouseenter', MAP_LAYERS.clusters, handleClusterMouseEnter);
        map.on('mouseleave', MAP_LAYERS.clusters, handleClusterMouseLeave);

        const setupStyleDependents = async () => {
            // A newer style switch can land while the marker images load; bail so a stale run doesn't touch the rebuilt style.
            const generation = ++styleGenerationRef.current;

            const markerImages = await loadMarkerImages();
            if (generation !== styleGenerationRef.current || mapRef.current !== map) return;

            installRuntimeLayers(map, markerImages, geojsonRef.current);
            applySelectionEmphasis(map, selectedDealIdRef.current, hoveredDealIdRef.current);

            if (!didInitialBoundsFitRef.current) {
                const bounds = getBoundsFromGeoJSON(geojsonRef.current);
                if (bounds) {
                    map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
                    didInitialBoundsFitRef.current = true;
                }
            }
        };

        // 'style.load' fires at init and again after every full setStyle rebuild, which wipes runtime images, sources, layers and terrain.
        map.on('style.load', setupStyleDependents);

        resizeObserverRef.current = new ResizeObserver(() => {
            if (resizeTimeoutRef.current) 
                clearTimeout(resizeTimeoutRef.current);
            
            resizeTimeoutRef.current = setTimeout(() => {
                resizeTimeoutRef.current = null;
                mapRef.current?.resize();
            }, 100);
        });
        resizeObserverRef.current.observe(containerRef.current);

        return () => {
            dealHoverPopup.remove();
            map.off('style.load', setupStyleDependents);
            map.off('click', MAP_LAYERS.unclusteredPoint, handlePinClick);
            map.off('click', MAP_LAYERS.clusters, handleClusterClick);
            map.off('click', handleMapClick);
            map.off('mouseenter', MAP_LAYERS.unclusteredPoint, handlePinMouseEnter);
            map.off('mouseleave', MAP_LAYERS.unclusteredPoint, handlePinMouseLeave);
            map.off('mouseenter', MAP_LAYERS.clusters, handleClusterMouseEnter);
            map.off('mouseleave', MAP_LAYERS.clusters, handleClusterMouseLeave);
            resizeObserverRef.current?.disconnect();
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const source = map.getSource(MAP_SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) return;
        
        source.setData(geojson);
        
        if (didInitialBoundsFitRef.current) return;

        const bounds = getBoundsFromGeoJSON(geojson);
        if (bounds) {
            map.fitBounds(bounds, { padding: MAP_BEHAVIOR.fitBoundsPadding, maxZoom: MAP_BEHAVIOR.maxZoom });
            didInitialBoundsFitRef.current = true;
        }
    }, [geojson]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        applySelectionEmphasis(map, selectedDealId, hoveredDealId);
    }, [selectedDealId, hoveredDealId]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || selectedDealId == null) return;

        const deal = deals.find(d => d.id === selectedDealId);
        if (!deal) return;
        const coords = getDealCoords(deal, geocodedCoordinates);
        if (coords) {
            const focusZoom = Math.min(MAP_BEHAVIOR.selectedDealZoom, MAP_BEHAVIOR.maxZoom);
            map.flyTo({
                center: [coords.lng, coords.lat],
                zoom: Math.max(map.getZoom(), focusZoom),
                duration: MAP_BEHAVIOR.zoomDuration,
            });
        }
    }, [selectedDealId, deals, geocodedCoordinates]);

    return { containerRef, handleFitAll, isSatellite, handleToggleSatellite };
};
